/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

/**
 * Makes a Klaviyo signup popup a membership signup as well.
 *
 * Klaviyo forms can't call our app or show data that isn't Klaviyo's own, but they render into
 * this page and announce submissions on `window` as `klaviyoForms` events. On a submit from one
 * of the configured forms, this creates the customer and the Yotpo membership through the
 * membership app, then writes the welcome code into the form wherever its success step says
 * [MEMBER_CODE].
 *
 * Klaviyo keeps the newsletter subscription and its consent, double opt-in included — nothing
 * here touches marketing consent.
 */

const JOIN_URL = '/apps/membership/join';
const CODE_URL = '/apps/membership/code';

// Typed as plain text into the Klaviyo success step, where the code should appear.
const PLACEHOLDER = '[MEMBER_CODE]';

// Yotpo issues the code a few seconds after the opt-in; past the timeout it is emailed.
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 45000;

// The success step can render after the submit event, so the placeholder is watched for a while.
const WATCH_MS = 60000;

class KlaviyoMembership extends HTMLElement {
  /** Emails already sent this page view, so a repeated submit event doesn't sign up twice. */
  #submitted = new Set();

  connectedCallback() {
    window.addEventListener('klaviyoForms', this.#handleKlaviyoForm);
  }

  disconnectedCallback() {
    window.removeEventListener('klaviyoForms', this.#handleKlaviyoForm);
  }

  /** @param {Event} event */
  #handleKlaviyoForm = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail ?? {};
    const formIds = (this.dataset.formIds ?? '').split(',').map((id) => id.trim());

    if (detail.type !== 'submit' || !formIds.includes(detail.formId)) return;

    const email = String(detail.metaData?.email ?? detail.metaData?.$email ?? '').trim();

    if (!email || this.#submitted.has(email)) return;

    this.#submitted.add(email);
    this.#signUp(email);
  };

  /** @param {string} email */
  async #signUp(email) {
    const slot = new CodeSlot(this.dataset.pending ?? '');

    try {
      const body = new FormData();
      body.set('email', email);

      const result = await request(JOIN_URL, { method: 'POST', body });

      // Someone already has an account with this email. Their code is theirs to see after
      // signing in; the app neither changes that customer nor reveals anything about them.
      if (result.status === 'existing') {
        slot.set(this.dataset.existing ?? '');
        return;
      }

      // Signed-in visitors join as themselves, and may already hold a code.
      if (result.welcomeCode) {
        slot.set(result.welcomeCode);
        return;
      }

      const code = await pollCode(result.claim);
      slot.set(code ?? this.dataset.emailed ?? '');
    } catch (error) {
      console.warn('[membership] popup signup failed', error);
      slot.set(this.dataset.emailed ?? '');
    }
  }
}

/**
 * Every text node in the page containing the placeholder, kept showing the current value.
 *
 * Text nodes rather than an element, because Klaviyo's markup is theirs and changes; the
 * placeholder is the one thing the form's author controls.
 */
class CodeSlot {
  /** @type {Map<Text, string>} Each node with the text it had before we filled it. */
  #nodes = new Map();
  #value;

  /** @param {string} value */
  constructor(value) {
    this.#value = value;
    this.#collect(document.body);
    this.#fill();

    // Only what changed is searched, so a busy page isn't walked in full on every mutation.
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'characterData') this.#collect(record.target);
        for (const node of record.addedNodes) this.#collect(node);
      }

      this.#fill();
    });

    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    setTimeout(() => observer.disconnect(), WATCH_MS);
  }

  /** @param {string} value */
  set(value) {
    this.#value = value;
    this.#fill();
  }

  /** @param {Node} root */
  #collect(root) {
    if (root instanceof Text) {
      const template = this.#nodes.get(root);

      // Klaviyo reused the node for other text since we filled it — it is no longer ours, and
      // filling it again would put the code back over whatever the form shows now.
      if (template !== undefined && root.data !== template.replaceAll(PLACEHOLDER, this.#value)) {
        this.#nodes.delete(root);
      }

      if (!this.#nodes.has(root) && root.data.includes(PLACEHOLDER)) this.#nodes.set(root, root.data);
      return;
    }

    if (!(root instanceof Element)) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      this.#collect(node);
    }
  }

  #fill() {
    for (const [text, template] of this.#nodes) {
      const filled = template.replaceAll(PLACEHOLDER, this.#value);

      // Writing our own value back would queue another mutation for nothing.
      if (text.data !== filled) text.data = filled;
    }
  }
}

/**
 * @param {string | undefined} claim Proof, for a visitor who isn't signed in, that the customer
 *   was created by their own request. Signed-in visitors are identified by Shopify instead.
 * @returns {Promise<string | null>}
 */
async function pollCode(claim) {
  const url = claim ? `${CODE_URL}?claim=${encodeURIComponent(claim)}` : CODE_URL;
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    const { code } = await request(url).catch(() => ({ code: null }));
    if (code) return code;
  }

  return null;
}

/**
 * @param {string} url
 * @param {RequestInit} [init]
 */
async function request(url, init = {}) {
  const response = await fetch(url, { ...init, headers: { accept: 'application/json' } });
  const body = await response.json().catch(() => ({}));

  if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);

  return body;
}

if (!customElements.get('klaviyo-membership')) customElements.define('klaviyo-membership', KlaviyoMembership);
